<script lang="ts">
export default {
  name: 'PolicyDeleteModal',
};
</script>

<script setup lang="ts">
import { useRoute } from 'vue-router';
import AppModal from './v2/AppModal.vue';
import { accountService } from '@/services/api';
import { documentService } from '@/services/v2/DocumentService';
import { Document } from '@/types/v2/Document';
import { computed, ref, watch } from 'vue';
import { relativeDateTime } from '@/utilities/Dates';
import { IsUserAClient } from '@/utilities/AuthUtils';
import Store from '@/store';
import { formatDate } from '@/utilities/Format';
import { Users } from 'lucide-vue-next';

const $route = useRoute();

const props = defineProps<{
  policyNumber: string;
  accountId: number;
  policyId: number;
  show?: boolean;
}>();

const emit = defineEmits<{
  'needs-update': [boolean];
  closed: [];
}>();

// -----
// refs
// -----
const documents = ref([] as Partial<Document>[]);
const confirmDocsDeletion = ref(false);
const showModal = ref(false);
const loading = ref(false);

// Watch for external show prop changes
watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      handleOpenModal();
    }
  },
);

// ---------
// computed
// ---------
const isClientRole = computed<boolean>(() => {
  return IsUserAClient(Store.state.user);
});

const hasAnyDocuments = computed<boolean>(() => {
  return documents.value.length > 0;
});

const modalText = computed<string>(() => {
  return hasAnyDocuments.value
    ? 'Are you sure you want to delete this policy and all its associated documents permanently?'
    : 'Are you sure you want to delete this policy?';
});

// --------
// methods
// --------
async function handleOpenModal() {
  showModal.value = true;
  confirmDocsDeletion.value = false;
  await loadDocuments();
}

async function handleCloseModal() {
  showModal.value = false;
  confirmDocsDeletion.value = false;
  documents.value = [];
  emit('closed');
}

async function handleConfirmDelete() {
  if (!confirmDocsDeletion.value) {
    return;
  }

  loading.value = true;
  try {
    await accountService.deletePolicy($route.params.id as string, props.policyNumber);
    emit('needs-update', true);
    showModal.value = false;
    emit('closed');
  } catch (error) {
    console.error('Failed to delete policy:', error);
  } finally {
    loading.value = false;
  }
}

async function loadDocuments(): Promise<void> {
  documents.value = await documentService.getDocumentsByPolicy(props.accountId, props.policyId);
  if (documents.value.length === 0) {
    confirmDocsDeletion.value = true;
  }
}
</script>

<template>
  <slot
    name="button"
    v-bind="{ onClick: handleOpenModal }">
  </slot>

  <AppModal
    title="Delete Policy"
    :show="showModal"
    :handle-cancel="handleCloseModal"
    :handle-action="handleConfirmDelete"
    action-button-label="Delete"
    :loading="loading"
    :action-disabled="!confirmDocsDeletion || loading"
    size="lg">
    <template #content>
      <div class="tw:flex tw:flex-col tw:gap-4">
        <span class="tw:text-xl tw:text-primary">
          {{ modalText }}
        </span>
        <!-- existing documents for policy -->
        <table
          v-if="documents.length > 0"
          class="tw:table tw:table-xs">
          <thead>
            <tr class="tw:border-b tw:border-solid tw:border-gray-200">
              <th>Name</th>
              <th v-show="!isClientRole"><!-- published status --></th>
              <th>Type</th>
              <th>Uploaded</th>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="doc in documents"
              :key="doc.documentId">
              <td>{{ doc.displayName }}</td>
              <th v-show="!isClientRole">
                <div
                  v-if="doc.published"
                  data-tip="This document is published and viewable by client users"
                  class="tw:tooltip tw:tooltip-right tw:top-0.5 tw:flex tw:justify-end">
                  <Users
                    class="tw:text-gray-500"
                    :size="16" />
                </div>
              </th>
              <td>{{ doc.documentTypeName }}</td>
              <td class="tw:flex tw:flex-col tw:items-start tw:justify-center">
                <span>{{ relativeDateTime(doc.createdByDate) }}</span>
                <span class="tw:text-xs tw:text-gray-600">{{ formatDate(doc.createdByDate) }}</span>
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Checkbox for confirmation -->
        <div
          v-if="hasAnyDocuments"
          class="tw:flex tw:gap-2">
          <input
            type="checkbox"
            id="confirmDeletion"
            v-model="confirmDocsDeletion"
            class="tw:checkbox tw:border tw:border-solid tw:border-gray-400 tw:checkbox-sm tw:checkbox-primary" />
          <label for="confirmDeletion"> I understand that the policy and all documents listed above will be permanently deleted. </label>
        </div>
      </div>
    </template>
  </AppModal>
</template>
